import { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Form } from "react-bootstrap";
import { InputGroup, Input, InputGroupText } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "./index.css";
import {
  useLazyGetMenuItemsByRestaurantIdQuery,
  useUpdateMenuItemAvailabilityMutation,
} from "../../../../redux/services/menuItemService";
import { MenuItem } from "../../../../redux/menuSlice";
import { DEFAULT_IMAGE_URL, SINGLE } from "../../../../constants/constans";
import { formatTimestamp } from "../../../../utility/Utility";
import WeekdayCalendar, {
  daysOfWeek,
} from "../../../../components/WeekDayCalender";
import ButtonComponent from "../../../../components/ButtonComponent";
import { setRowsPerPage } from "../../../../redux/configurationMenuItemSlice";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import SavingButton from "../../../../components/SavingMessageComponent";

const MenuItemAvailableDays: React.FC = () => {
  const dispatch = useDispatch();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const rowsPerPage = useSelector((state: any) => state.configurationMenuItem.rowsPerPage);

  const [selectAllDays, setSelectAllDays] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);

  const [trigger, { data: getMenuItemsResponse, isLoading }] =
    useLazyGetMenuItemsByRestaurantIdQuery();
  const [updateMenuItemAvailability] = useUpdateMenuItemAvailabilityMutation();

  useEffect(() => {
    if (restaurantId) {
      trigger(restaurantId);
    }
  }, [restaurantId, trigger]);

  useEffect(() => {
    if (getMenuItemsResponse) {
      const items = getMenuItemsResponse.flatMap(
        (category: any) => category.items
      );
      const itemsWithAvailableDays = items.map((item: MenuItem) => ({
        ...item,
        availableDays: item.availableDays || [],
      }));
      setMenuItems(itemsWithAvailableDays);
    }
  }, [getMenuItemsResponse]);

  // Filter items based on search term (flexible search - words can be in any order)
  useEffect(() => {
    if (menuItems && menuItems.length > 0) {
      if (searchTerm.trim() === "") {
        setFilteredItems(menuItems);
      } else {
        const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
        const filtered = menuItems.filter((item: MenuItem) => {
          const searchableText = [
            item.displayName || "",
            item.productId || "",
          ].join(" ").toLowerCase();
          return searchWords.every(word => searchableText.includes(word));
        });
        setFilteredItems(filtered);
      }
    }
  }, [menuItems, searchTerm]);

  const setAvailableDays = (id: string, days: number[]) => {
    setMenuItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, availableDays: days } : item
      )
    );
    setHasChanges(true);
  };

  useEffect(() => {
    const allDaysSelected = menuItems.every(
      (item) => item.availableDays?.length === Object.keys(daysOfWeek).length
    );
    setSelectAllDays(allDaysSelected);
  }, [menuItems]);

  const handleSelectAllDays = () => {
    setMenuItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        availableDays: selectAllDays
          ? []
          : Object.entries(daysOfWeek).map((value) => value[1]),
      }))
    );
    setSelectAllDays(!selectAllDays);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    const menuItemsData = menuItems.map((item) => ({
      itemId: item.id,
      availableDays: item.availableDays,
    }));
    const requestData = { restaurantId, menuType: SINGLE, menuItemsData };

    try {
      await updateMenuItemAvailability(requestData).unwrap();
      toast.success("Availability updated successfully!");
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to update availability.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const columns: TableColumn<MenuItem>[] = [
    {
      name: <h6 className="fw-bold">Image</h6>,
      selector: (row) => row.imageUrl,
      cell: (row) => (
        <img
          src={row.imageUrl.length > 0 ? row.imageUrl[0] : DEFAULT_IMAGE_URL}
          alt={row.displayName || ""}
          className="menuitem-image"
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      cell: (row) => (
        <div>
          <div>{row.displayName}</div>
        </div>
      ),
    },
    {
      name: <h6 className="fw-bold">Price(Rs.)</h6>,
      selector: (row) => row.price,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row) => formatTimestamp(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Availability</h6>,
      cell: (row) => (
        <div className="menuitem-availability">
          <WeekdayCalendar
            availableDays={row.availableDays}
            setAvailableDays={(days) => setAvailableDays(row.id, days)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading ? (
        <div className="bg-white">
          <TableLoadingComponent numColumns={5} />
        </div>
      ) : (
        <>
          <div className="menuitem-save-change mt-2 pb-2 d-flex align-items-center">
            <Form.Check
              type="checkbox"
              label={selectAllDays ? "Deselect All" : "Select All"}
              checked={selectAllDays}
              onChange={handleSelectAllDays}
              style={{ paddingLeft: "3%" }}
              className="me-3"
            />
            <div>
              {isSaving ? (<SavingButton isSaving={true} />
              ) : (<ButtonComponent
                titel={"Save"}
                onClick={handleSaveChanges}
                isDisabled={false}
              />)}
              {hasChanges && (
                <div className="mt-1 text-warning">Save changes</div>
              )}
            </div>
          </div>

          {/* Search Input */}
          <div className="p-3 bg-white">
            <InputGroup>
              <InputGroupText>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
              <Input
                type="text"
                placeholder="Search menu items by name or SKU ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </InputGroup>
          </div>

          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            highlightOnHover
            paginationPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            noDataComponent={
              <div className="py-4 text-center">
                {searchTerm ? `No menu items found for "${searchTerm}"` : "No menu items available"}
              </div>
            }
          />
        </>
      )}
    </>
  );
};

export default MenuItemAvailableDays;
